import { type AxiosError } from 'axios'
import { makeAutoObservable } from 'mobx'

import { AdminsController } from '@App/api'
import {
    type INewAdminDto,
    type IAdmin,
    type IUpdateAdminDto,
    type IPriceDto,
    type IFedOrderDto
} from '@App/types/adminsTypes'
import { EToastings, type IApiErrorHandler } from '@App/types/common'
import { onHandleResponseNotification, onToasting, onUseQueries } from '@App/utils'

import { onBuildPriceFile } from './helper'

class Admins {
    admins: IAdmin[] | null = null
    activateDates: string[] | null = null

    priceErrors: string[] = []

    isFetchingAdmins: boolean = false

    constructor() {
        makeAutoObservable(this)
    }

    // actions
    onSetIsFetchingAdmins(isFetchingAdmins: boolean) {
        this.isFetchingAdmins = isFetchingAdmins
    }

    onSetAdmins(admins: IAdmin[] | null) {
        this.admins = admins
    }

    onSetActivateDates(activateDates: string[] | null) {
        this.activateDates = activateDates
    }

    onSetPriceErrors = (priceErrors: string[]) => {
        this.priceErrors = priceErrors
    }

    // requests
    async onGetAdmins() {
        this.onSetIsFetchingAdmins(true)
        const client = new AdminsController()
        try {
            const { data } = await client.onGetAdmins()
            if (data.value) {
                this.onSetAdmins(data.value)
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetchingAdmins(false)
        }
    }

    onAddAdminRole = async (login: string, roleName: string, onSuccess: () => void) => {
        this.onSetIsFetchingAdmins(true)
        const client = new AdminsController()
        const queries = onUseQueries({ login, roleName })
        try {
            const { status } = await client.onAddAdminRole(queries)
            if (status >= 200 && status < 300) {
                onHandleResponseNotification(status, `Администратору "${login}" успешно добавлено право доступа`)
                onSuccess()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetchingAdmins(false)
        }
    }

    onRemoveAdminRole = async (login: string, roleName: string, onSuccess: () => void) => {
        this.onSetIsFetchingAdmins(true)
        const client = new AdminsController()
        const queries = onUseQueries({ login, roleName })
        try {
            const { status } = await client.onRemoveAdminRole(queries)
            if (status >= 200 && status < 300) {
                onHandleResponseNotification(status, `У администратора "${login}" успешно изъято право доступа`)
                onSuccess()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetchingAdmins(false)
        }
    }

    async onUpdateAdmin(dto: IUpdateAdminDto) {
        this.onSetIsFetchingAdmins(true)
        const client = new AdminsController()
        try {
            const { status } = await client.onUpdateAdmin(dto)
            onHandleResponseNotification(status, `Админ ${dto.login} успешно изменен`)
            this.onSetAdmins(null)
            this.onGetAdmins()
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetchingAdmins(false)
        }
    }

    async onAddAdmin(dto: INewAdminDto) {
        this.onSetIsFetchingAdmins(true)
        const client = new AdminsController()
        try {
            const { status } = await client.onAddAdmin(dto)
            onHandleResponseNotification(status, `Новый администратор ${dto.login} успешно создан`)
            this.onSetAdmins(null)
            this.onGetAdmins()
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            // TODO: Уточнить про нормальный обработчик ошибок
            if (error.response?.status === 409) {
                onToasting({
                    type: EToastings.ERROR,
                    message: error.response?.data?.detail || 'Не обработано'
                })
            } else {
                onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
            }
        } finally {
            this.onSetIsFetchingAdmins(false)
        }
    }

    async onRemoveAdmin(login: string) {
        this.onSetIsFetchingAdmins(true)
        const client = new AdminsController()
        const query = onUseQueries({ login })
        try {
            const { status } = await client.onRemoveAdmin(query)
            onHandleResponseNotification(status, `Админ ${login} успешно удален`)
            this.onSetAdmins(null)
            this.onGetAdmins()
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetchingAdmins(false)
        }
    }

    async onGetPriceActivateDates() {
        const client = new AdminsController()
        this.onSetIsFetchingAdmins(true)
        try {
            const { data } = await client.onGetPriceActivateDates()
            this.onSetActivateDates(data)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetchingAdmins(false)
        }
    }

    async onUploadPriceFileCallback(dto: IPriceDto, onFinite: () => void) {
        const client = new AdminsController()
        try {
            const { status } = await client.onUploadPriceFileCallback(dto)
            onHandleResponseNotification(status, `Прайс успешно обновлен`)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            // TODO: Уточнить обработчик ошибок на беке и оформить в общий обработчик
            if (
                error?.response?.status === 400 &&
                typeof error?.response?.data?.detail === 'string' &&
                error?.response?.data?.detail
            ) {
                this.onSetPriceErrors(JSON.parse(error?.response?.data?.detail))
            } else {
                onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
            }
        } finally {
            onFinite()
        }
    }

    async onGetPriceDataByDate(date: string) {
        const client = new AdminsController()
        this.onSetIsFetchingAdmins(true)
        try {
            const { data } = await client.onGetPriceDataByDate(date)
            onBuildPriceFile(data, date)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetchingAdmins(false)
        }
    }

    async onUploadOrderFileCallback(dto: IFedOrderDto, onFinite: () => void) {
        const client = new AdminsController()
        try {
            const { status } = await client.onUploadOrderFileCallback(dto)
            onHandleResponseNotification(status, `Федеральный приказ успешно обновлен`)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            // TODO: Уточнить обработчик ошибок на беке и оформить в общий обработчик
            if (
                error?.response?.status === 400 &&
                typeof error?.response?.data?.detail === 'string' &&
                error?.response?.data?.detail
            ) {
                this.onSetPriceErrors(JSON.parse(error?.response?.data?.detail))
            } else {
                onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
            }
        } finally {
            onFinite()
        }
    }

    async onChangeDocument(file: Blob, fileName: string, onSuccess: () => void) {
        const client = new AdminsController()
        const queries = onUseQueries({ kind: 'document' })
        const dto = new FormData()
        dto.append('file', file)
        try {
            const { data } = await client.onChangeDocument(dto, fileName, queries)
            if (data.location) {
                onSuccess()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    // TODO: разобраться с ролями админа по апи
}

export default new Admins()
